import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Group } from '@shared/models/group';
import { GroupFilters } from '@shared/models/group-filters';

@Component({
  selector: 'app-lead-customer-selector-item',
  templateUrl: './lead-customer-selector-item.component.html',
  standalone: false,
})
export class LeadCustomerSelectorItemComponent implements OnInit {
  @Input() group!: Group;
  @Input() groupFilters!: GroupFilters;
  @Input() selected = false;
  @Output() selectedGroup = new EventEmitter<Group>();

  nameSegments: Array<{ text: string; highlight: boolean }> = [];
  emailSegments: Array<{ text: string; highlight: boolean }> = [];
  primaryEmail = '';
  tooltip = '';

  ngOnInit(): void {
    this.primaryEmail =
      this.group.attributes.contacts?.find((contact) => contact.contact_roles?.includes('primary'))?.email || '';
    this.emailSegments = this.getEmailSegments();
    this.nameSegments = this.highlightSegments(this.group.attributes.name, this.groupFilters.duplicates_for_name);
    this.tooltip = this.getMatchTooltip();
  }

  getEmailSegments(): Array<{ text: string; highlight: boolean }> {
    const email = this.primaryEmail;
    const domain = this.groupFilters.duplicates_for_email_domain;
    if (!email || !domain || !email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)) {
      return [{ text: email, highlight: false }];
    }

    const atIndex = email.lastIndexOf('@');
    return [
      { text: email.slice(0, atIndex + 1), highlight: false },
      { text: email.slice(atIndex + 1), highlight: true },
    ];
  }

  highlightSegments(value: string, filter?: string): Array<{ text: string; highlight: boolean }> {
    const tokens = filter ? this.significantTokens(filter) : [];
    if (!value || !tokens.length) {
      return [{ text: value, highlight: false }];
    }

    const safeWords = tokens.map((token) => token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const wordPattern = safeWords.join('|');
    const fullNamePattern = safeWords.join('[^a-zA-Z0-9]+');
    const splitPattern = new RegExp(`(${fullNamePattern}|${wordPattern})`, 'gi');
    const matchingPattern = new RegExp(`^(?:${fullNamePattern}|${wordPattern})$`, 'i');
    return value
      .split(splitPattern)
      .filter(Boolean)
      .map((text) => ({ text, highlight: matchingPattern.test(text) }));
  }

  getMatchTooltip(): string {
    const reasons = this.matchReasons();
    const labels = reasons.map((reason) => {
      switch (reason) {
        case 'name':
          return 'Name';
        case 'email_domain':
          return 'Email domain';
        case 'company_registration_number':
          return 'CRN';
        case 'vat_number':
          return 'VAT';
        default:
          return reason;
      }
    });
    const matchSummary =
      labels.length === 0
        ? 'similar customer details'
        : labels.length === 1
          ? labels[0]
          : labels.length === 2
            ? labels.join(' and ')
            : `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
    const details = [`Matches this lead on ${matchSummary}.`];
    if (reasons.includes('company_registration_number')) {
      details.push(`CRN ${this.group.attributes.company_registration_number}.`);
    }
    if (reasons.includes('vat_number')) {
      details.push(`VAT ${this.group.attributes.vat_number}.`);
    }
    return details.join(' ');
  }

  matchReasons(): string[] {
    const { attributes } = this.group;
    const {
      duplicates_for_name: duplicateName,
      duplicates_for_email_domain: duplicateEmailDomain,
      duplicates_for_company_registration_number: duplicateCompanyRegistrationNumber,
      duplicates_for_vat_number: duplicateVatNumber,
    } = this.groupFilters;

    return [
      duplicateName &&
      this.significantTokens(duplicateName).some((token) => this.normalise(attributes.name).includes(token))
        ? 'name'
        : undefined,
      duplicateEmailDomain && this.normalise(this.primaryEmail).endsWith(`@${this.normalise(duplicateEmailDomain)}`)
        ? 'email_domain'
        : undefined,
      duplicateCompanyRegistrationNumber &&
      this.normalise(attributes.company_registration_number) === this.normalise(duplicateCompanyRegistrationNumber)
        ? 'company_registration_number'
        : undefined,
      duplicateVatNumber && this.normalise(attributes.vat_number) === this.normalise(duplicateVatNumber)
        ? 'vat_number'
        : undefined,
    ].filter((reason): reason is string => Boolean(reason));
  }

  normalise(value?: string | null): string {
    return (value || '').trim().toLowerCase();
  }

  significantTokens(value: string): string[] {
    return value
      .split(/[^a-zA-Z0-9]+/)
      .map((token) => token.trim().toLowerCase())
      .filter((token) => token.length >= 2);
  }

  selectGroup(): void {
    this.selectedGroup.emit(this.group);
  }
}
